import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { differenceInWeeks, format, isAfter } from 'date-fns';
import { UserManager } from '@app/modules/shared/managers/user.manager';
import { Action, NavigationDataKey, NavigationType, UserPermissions } from '@shared-libs/enums';
import { NavigationService } from '@shared-services/navigation.service';
import {
	DateHelper,
	translate,
	userWantsRecurringActionOnDeletingEvent,
	userWantsRecurringActionOnEvent,
} from '@shared-libs/helpers';
import { DisplayCalendarEvent, ICalendarEventQuery } from '@shared-libs/interfaces';
import { NavigationDataManager } from '@shared-managers/navigation-data.manager';
import { CalendarEventProvider } from '@shared-providers/api/calendar-event.provider';
import { CalendarEventType, ICalendarEvent } from '@shared-models/calendar-event.model';
import { DisplayCalendarEventAdapter } from '@shared/adapters/display-calendar-event.adapter';
import {
	ExtendedCalendarView,
	ICalendarTranslations,
	CalendarEventAction,
	CalendarEventTimesChangedEvent,
} from '@beego/ngx-calendar';
import { IEvent } from '@internal-models/event.model';
import { EventComponent } from '@internal-components/event-components/event-component/event.component';
import { EventProvider } from '@internal-providers/event.provider';
import { EventDetailsComponent } from '@internal-components/event-components/event-details-component/event-details.component';
import { IEmployee } from '@internal-models/employee.model';
import { CreateBeegoAppointmentComponent } from '@internal-components/beego-appointment-components/create-beego-appointment-component/create-beego-appointment.component';
import { BeegoAppointmentProvider } from '@internal-providers/beego-appointment.provider';
import { BeegoAppointmentDetailsComponent } from '@internal-components/beego-appointment-components/beego-appointment-details-component/beego-appointment-details.component';
import { isEmpty, omit } from 'lodash';
import { CalendarEventReservationsProvider } from '@internal-providers/calendar-event-reservations.provider';

@Injectable({
	providedIn: 'root',
})
export class CalendarController {
	public view: ExtendedCalendarView = ExtendedCalendarView.Week;
	public viewDate: Date = new Date();
	public events: Array<DisplayCalendarEvent> = new Array();
	public query: ICalendarEventQuery = {} as ICalendarEventQuery;
	public action: Action;
	public beegoNoPartnerLink: boolean = false;
	public beegoStudentsAreMissing: boolean = false;
	public firstBeegoStudentDate: string;
	public filtering: boolean = false;
	public readonly ExtendedCalendarView: typeof ExtendedCalendarView = ExtendedCalendarView;
	public readonly refresh: Subject<any> = new Subject();
	public readonly translations: ICalendarTranslations = {
		month: translate('CALENDAR.MONTH'),
		week: translate('CALENDAR.WEEK'),
		workWeek: translate('CALENDAR.WORK_WEEK'),
		day: translate('CALENDAR.DAY'),
		today: translate('CALENDAR.TODAY'),
		date: translate('CALENDAR.DATE'),
	};
	public readonly actions: Array<CalendarEventAction> = [];

	constructor(
		public readonly userManager: UserManager,
		private readonly navigationService: NavigationService,
		private readonly navigationDataManager: NavigationDataManager,
		private readonly calendarEventProvider: CalendarEventProvider,
		private readonly eventProvider: EventProvider,
		private readonly beegoAppointmentProvider: BeegoAppointmentProvider,
		private readonly calendarEventReservationsProvider: CalendarEventReservationsProvider
	) {
		this.navigationService.onNavigateToCurrentPage().subscribe(() => {
			this.handleCalendarPageEntry();
		});
	}

	public handleCalendarPageEntry(): void {
		this.action = this.navigationDataManager.getData(NavigationDataKey.action);
		void this.getDisplayCalendarEvents();
	}

	public async getDisplayCalendarEvents(query: Partial<ICalendarEventQuery> = this.query): Promise<void> {
		const dateRange = DateHelper.getDateRange(this.viewDate, this.view);
		this.query = { ...this.query, ...query, dateRange };
		this.events = (await this.calendarEventProvider.getAll(this.query)).map((event: ICalendarEvent) =>
			DisplayCalendarEventAdapter.fromCalendarEvent(event, this.actions)
		);
		this.refresh.next(this.events);
		this.setIsFilteringState(this.query);
		void this.setBeegoStudentsMissingState(this.query);
	}

	public onViewDateChanged(date: Date): void {
		this.viewDate = date;
		void this.getDisplayCalendarEvents();
	}

	public onDayChanged(date: Date): void {
		this.viewDate = date;
		this.view = ExtendedCalendarView.Day;
	}

	public onViewChanged(view: ExtendedCalendarView): void {
		this.view = view;
		void this.getDisplayCalendarEvents();
	}

	public async onHourSegmentClick(date: Date): Promise<void> {
		if (this.userManager.hasAllPermissions([UserPermissions.WriteEvents])) {
			if (isAfter(date, new Date())) {
				await this.showEventDetails(null, date);
			}
		}
	}

	public async onEventClick({ event }: { click: PointerEvent; event: DisplayCalendarEvent }): Promise<void> {
		switch (event.meta.calendarEvent.calendarEventType) {
			case CalendarEventType.InternalEvent:
				const Event = await this.eventProvider.getOneForDay(
					event.meta.calendarEvent.Event.id,
					event.meta.calendarEvent.id
				);
				return this.navigationService.navigateTo(NavigationType.modal, EventDetailsComponent, {
					calendarEvent: event.meta.calendarEvent,
					event: Event,
				});
			case CalendarEventType.BeegoAppointment:
				return this.openBeegoAppointmentCalendarEvent(event.meta.calendarEvent);
		}
	}

	/**
	 * Update an event. This is only available for internal events
	 * @deprecated
	 * @param root0 The CalendarEvent drag object
	 * @param root0.event The actual CalendarEvent
	 * @param root0.newStart The updated start datetime
	 * @param root0.newEnd The updated end datetime
	 * @returns A promise
	 */
	public async onEventDrag({
		event,
		newStart,
		newEnd,
	}: CalendarEventTimesChangedEvent<{ calendarEvent: ICalendarEvent }>): Promise<void> {
		event.start = newStart;
		event.end = newEnd;

		const Event = await this.eventProvider.getOne(event.meta.calendarEvent.Event.id);
		const calendarEvent = event.meta.calendarEvent;
		Event.startsAt = event.start;
		Event.endsAt = event.end;

		const isRecurring = await userWantsRecurringActionOnEvent(Event.interval, Action.UPDATE);

		if (isRecurring === null) {
			return this.getDisplayCalendarEvents();
		}

		try {
			await this.eventProvider.update(
				Event.id,
				{ ...Event, Employees: Event.Employees.map((employee: IEmployee) => employee.id) },
				calendarEvent,
				isRecurring
			);
		} catch (err) {
			void this.getDisplayCalendarEvents();
		}
	}

	public async onEventDelete(calendarEvent: DisplayCalendarEvent): Promise<void> {
		const isRecurring = await userWantsRecurringActionOnDeletingEvent(
			calendarEvent.meta.calendarEvent.Event.interval
		);

		if (isRecurring !== null) {
			await this.eventProvider.delete(
				calendarEvent.meta.calendarEvent.Event.id,
				calendarEvent.meta.calendarEvent,
				isRecurring
			);
		}

		void this.getDisplayCalendarEvents();
	}

	public async onEventUpdate(event: DisplayCalendarEvent): Promise<void> {
		const Event = await this.eventProvider.getOne(event.meta.calendarEvent.Event.id);
		Event.CalendarEvent = event.meta.calendarEvent;
		await this.showEventDetails(Event);
	}

	public async showEventDetails(event?: IEvent, clickedDate?: Date): Promise<void> {
		void this.navigationService
			.navigateTo(NavigationType.modal, EventComponent, {
				event,
				action: event ? Action.UPDATE : Action.CREATE,
				date: clickedDate,
			})
			.then(async () => {
				setTimeout(() => {
					void this.getDisplayCalendarEvents();
				}, 100);
			})
			.catch();
	}

	/**
	 * Open the correct modal or popover for a calendar event with type BeegoAppointment.
	 * When the calendar event has a BeegoAppointmentId, meaning a BEEGO appointment is booked, we open the
	 * BeegoAppointmentDetailsComponent otherwise we open the CreateBeegoAppointmentComponent
	 * @param calendarEvent The calendar event
	 * @returns A promise
	 */
	private async openBeegoAppointmentCalendarEvent(calendarEvent: ICalendarEvent): Promise<void> {
		if (calendarEvent.BeegoAppointmentId) {
			const beegoAppointment = await this.beegoAppointmentProvider.getOne(
				calendarEvent.BeegoAppointment.AppointmentId
			);
			return this.navigationService.navigateTo(NavigationType.modal, BeegoAppointmentDetailsComponent, {
				beegoAppointment,
			});
		} else {
			let beego = await this.calendarEventReservationsProvider.reserve(calendarEvent.BeecloudCalendarEvent.id);

			return this.navigationService
				.navigateTo<{ action?: Action }>(NavigationType.modal, CreateBeegoAppointmentComponent, {
					calendarEvent,
					address: this.query.address,
					calendarEventReservationEnd: new Date(beego.expiresAt), //add(new Date(), {minutes: 15,}),
				})
				.then((result) => {
					this.calendarEventReservationsProvider.release();
					if (result?.action === Action.CREATE) {
						void this.getDisplayCalendarEvents();
					}
				});
		}
	}

	/**
	 * Method to set the beegoStudentsAreMissing boolean to show and hide the warning message
	 * @param query The calendar events query
	 */
	private async setBeegoStudentsMissingState(query: Partial<ICalendarEventQuery>): Promise<void> {
		this.beegoNoPartnerLink = false;
		const beegoStudentsShouldBePresent = query.zipcode && (query.beego || query.beego === undefined);

		if (beegoStudentsShouldBePresent) {
			try {
				let firstDate = await this.calendarEventProvider.getDateOfFirstAvailableBeegoStudent(query.zipcode);

				firstDate = firstDate ? new Date(firstDate) : null;

				if (firstDate && differenceInWeeks(new Date(), firstDate) < 4) {
					this.beegoStudentsAreMissing = false;
					this.firstBeegoStudentDate = format(firstDate, 'dd/MM/yyyy');
				} else {
					this.beegoStudentsAreMissing = true;
					this.firstBeegoStudentDate = null;
				}
			} catch (error) {
				if (error.status === 400) {
					this.beegoNoPartnerLink = true;
				}
			}
		} else {
			this.beegoStudentsAreMissing = false;
			this.firstBeegoStudentDate = null;
		}
	}

	/**
	 * Method to set the Filtering boolean to show and hide the filters bar
	 * @param query The calendar events query
	 */
	private setIsFilteringState(query: Partial<ICalendarEventQuery>): void {
		this.filtering = false;
		let shortQuery = omit(query, ['dateRange']);
		for (const filter of Object.values(shortQuery)) {
			if (!isEmpty(filter)) {
				this.filtering = true;
			}
		}
	}
}
